import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/**
 * Climate risk register + scenario analysis (C-01/C-02). Both render into
 * the AASB S2 report: risks under Risk management, scenarios under the
 * Strategy section's climate-resilience requirement.
 */
interface Risk {
	riskID: number;
	name: string;
	kind: "physical" | "transition" | "opportunity";
	horizon: "short" | "medium" | "long";
	likelihood: number;
	consequence: number;
	description: string | null;
	mitigation: string | null;
	isMaterial: number;
	scenarioNotes: string | null;
}

interface Scenario {
	scenarioID: number;
	name: string;
	temperature: string | null;
	assumptions: string | null;
	resilienceSummary: string | null;
}

const emptyRisk = () => ({
	name: "",
	kind: "transition" as Risk["kind"],
	horizon: "medium" as Risk["horizon"],
	likelihood: "3",
	consequence: "3",
	description: "",
	mitigation: "",
	isMaterial: false,
});

const emptyScenario = () => ({
	name: "",
	temperature: "1.5°C",
	assumptions: "",
	resilienceSummary: "",
});

const ratingColor = (r: number): "success" | "warning" | "error" =>
	r >= 15 ? "error" : r >= 8 ? "warning" : "success";

const ClimateRisks: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [risks, setRisks] = useState<Risk[]>([]);
	const [scenarios, setScenarios] = useState<Scenario[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [riskDialog, setRiskDialog] = useState<null | { id?: number }>(null);
	const [riskForm, setRiskForm] = useState(emptyRisk());
	const [scenarioDialog, setScenarioDialog] = useState<null | { id?: number }>(null);
	const [scenarioForm, setScenarioForm] = useState(emptyScenario());
	const [saving, setSaving] = useState(false);

	const authFetch = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			return fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await authFetch("/api/climate-risks");
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not load the register");
			setRisks((json.Data || json).risks || []);
			setScenarios((json.Data || json).scenarios || []);
		} catch (e: any) {
			setError(e.message || "Could not load the register");
		} finally {
			setLoading(false);
		}
	}, [authFetch]);

	useEffect(() => {
		load();
	}, [load]);

	const saveRisk = async () => {
		setSaving(true);
		setError(null);
		try {
			const res = await authFetch(
				riskDialog?.id ? `/api/climate-risks/${riskDialog.id}` : "/api/climate-risks",
				{
					method: riskDialog?.id ? "PUT" : "POST",
					body: JSON.stringify(riskForm),
				},
			);
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not save the risk");
			setRiskDialog(null);
			await load();
		} catch (e: any) {
			setError(e.message || "Could not save the risk");
		} finally {
			setSaving(false);
		}
	};

	const saveScenario = async () => {
		setSaving(true);
		setError(null);
		try {
			const res = await authFetch(
				scenarioDialog?.id
					? `/api/climate-scenarios/${scenarioDialog.id}`
					: "/api/climate-scenarios",
				{
					method: scenarioDialog?.id ? "PUT" : "POST",
					body: JSON.stringify(scenarioForm),
				},
			);
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not save the scenario");
			setScenarioDialog(null);
			await load();
		} catch (e: any) {
			setError(e.message || "Could not save the scenario");
		} finally {
			setSaving(false);
		}
	};

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
			)}
			<Paper elevation={2} sx={{ p: 4, mb: 3 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
					<Box sx={{ flex: 1, minWidth: 260 }}>
						<Typography variant="h5">Climate risk register</Typography>
						<Typography variant="body2" color="text.secondary">
							Identify climate-related risks and opportunities, rate
							them on a 5×5 likelihood × consequence matrix and flag
							the material ones. The register renders in the S2
							report's Risk management section.
						</Typography>
					</Box>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={() => {
							setRiskForm(emptyRisk());
							setRiskDialog({});
						}}
					>
						Add risk
					</Button>
				</Box>
				{loading ? (
					<Box sx={{ textAlign: "center", py: 5 }}><CircularProgress /></Box>
				) : risks.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
						No risks recorded yet.
					</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Risk / opportunity</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>Horizon</TableCell>
								<TableCell align="right">Rating</TableCell>
								<TableCell>Material</TableCell>
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{risks.map((r) => (
								<TableRow key={r.riskID}>
									<TableCell>
										<strong>{r.name}</strong>
										{r.mitigation && (
											<Typography variant="caption" display="block" color="text.secondary">
												Response: {r.mitigation}
											</Typography>
										)}
									</TableCell>
									<TableCell sx={{ textTransform: "capitalize" }}>{r.kind}</TableCell>
									<TableCell sx={{ textTransform: "capitalize" }}>{r.horizon} term</TableCell>
									<TableCell align="right">
										<Chip
											size="small"
											color={ratingColor(r.likelihood * r.consequence)}
											label={`${r.likelihood}×${r.consequence} = ${r.likelihood * r.consequence}`}
										/>
									</TableCell>
									<TableCell>{r.isMaterial ? <Chip size="small" color="info" label="Material" /> : "—"}</TableCell>
									<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
										<IconButton
											size="small"
											aria-label="edit"
											onClick={() => {
												setRiskForm({
													name: r.name,
													kind: r.kind,
													horizon: r.horizon,
													likelihood: String(r.likelihood),
													consequence: String(r.consequence),
													description: r.description || "",
													mitigation: r.mitigation || "",
													isMaterial: !!r.isMaterial,
												});
												setRiskDialog({ id: r.riskID });
											}}
										>
											<EditOutlinedIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											aria-label="delete"
											onClick={async () => {
												await authFetch(`/api/climate-risks/${r.riskID}`, { method: "DELETE" });
												await load();
											}}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</Paper>

			<Paper elevation={2} sx={{ p: 4 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
					<Box sx={{ flex: 1, minWidth: 260 }}>
						<Typography variant="h5">Scenario analysis</Typography>
						<Typography variant="body2" color="text.secondary">
							Assess resilience under at least two scenarios,
							including a 1.5°C scenario. Scenarios render in the S2
							report's Strategy section.
						</Typography>
					</Box>
					<Button
						variant="outlined"
						startIcon={<AddIcon />}
						onClick={() => {
							setScenarioForm(emptyScenario());
							setScenarioDialog({});
						}}
					>
						Add scenario
					</Button>
				</Box>
				{scenarios.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
						No scenarios yet — AASB S2 expects at least two.
					</Typography>
				) : (
					scenarios.map((sc) => (
						<Paper key={sc.scenarioID} variant="outlined" sx={{ p: 2, mb: 2 }}>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								<Typography variant="subtitle1" sx={{ flex: 1 }}>
									<strong>{sc.name}</strong>
									{sc.temperature ? ` (${sc.temperature})` : ""}
								</Typography>
								<IconButton
									size="small"
									aria-label="edit"
									onClick={() => {
										setScenarioForm({
											name: sc.name,
											temperature: sc.temperature || "",
											assumptions: sc.assumptions || "",
											resilienceSummary: sc.resilienceSummary || "",
										});
										setScenarioDialog({ id: sc.scenarioID });
									}}
								>
									<EditOutlinedIcon fontSize="small" />
								</IconButton>
								<IconButton
									size="small"
									aria-label="delete"
									onClick={async () => {
										await authFetch(`/api/climate-scenarios/${sc.scenarioID}`, { method: "DELETE" });
										await load();
									}}
								>
									<DeleteOutlineIcon fontSize="small" />
								</IconButton>
							</Box>
							{sc.assumptions && (
								<Typography variant="body2" sx={{ mt: 1 }}>{sc.assumptions}</Typography>
							)}
							{sc.resilienceSummary && (
								<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
									<em>Resilience:</em> {sc.resilienceSummary}
								</Typography>
							)}
						</Paper>
					))
				)}
			</Paper>

			<Dialog open={riskDialog !== null} onClose={() => setRiskDialog(null)} fullWidth maxWidth="sm">
				<DialogTitle>{riskDialog?.id ? "Edit risk" : "Add risk"}</DialogTitle>
				<DialogContent dividers sx={{ display: "grid", gap: 2, pt: 2 }}>
					<TextField size="small" label="Name" value={riskForm.name} onChange={(e) => setRiskForm({ ...riskForm, name: e.target.value })} required sx={{ mt: 1 }} />
					<Box sx={{ display: "flex", gap: 2 }}>
						<TextField size="small" select label="Type" value={riskForm.kind} onChange={(e) => setRiskForm({ ...riskForm, kind: e.target.value as Risk["kind"] })} sx={{ flex: 1 }}>
							<MenuItem value="physical">Physical</MenuItem>
							<MenuItem value="transition">Transition</MenuItem>
							<MenuItem value="opportunity">Opportunity</MenuItem>
						</TextField>
						<TextField size="small" select label="Horizon" value={riskForm.horizon} onChange={(e) => setRiskForm({ ...riskForm, horizon: e.target.value as Risk["horizon"] })} sx={{ flex: 1 }}>
							<MenuItem value="short">Short term (1–2 yrs)</MenuItem>
							<MenuItem value="medium">Medium term (3–5 yrs)</MenuItem>
							<MenuItem value="long">Long term (5+ yrs)</MenuItem>
						</TextField>
					</Box>
					<Box sx={{ display: "flex", gap: 2 }}>
						<TextField size="small" select label="Likelihood (1–5)" value={riskForm.likelihood} onChange={(e) => setRiskForm({ ...riskForm, likelihood: e.target.value })} sx={{ flex: 1 }}>
							{["1", "2", "3", "4", "5"].map((v) => (
								<MenuItem key={v} value={v}>{v}</MenuItem>
							))}
						</TextField>
						<TextField size="small" select label="Consequence (1–5)" value={riskForm.consequence} onChange={(e) => setRiskForm({ ...riskForm, consequence: e.target.value })} sx={{ flex: 1 }}>
							{["1", "2", "3", "4", "5"].map((v) => (
								<MenuItem key={v} value={v}>{v}</MenuItem>
							))}
						</TextField>
					</Box>
					<TextField size="small" label="Description (optional)" value={riskForm.description} onChange={(e) => setRiskForm({ ...riskForm, description: e.target.value })} multiline minRows={2} />
					<TextField size="small" label="Response / mitigation (optional)" value={riskForm.mitigation} onChange={(e) => setRiskForm({ ...riskForm, mitigation: e.target.value })} multiline minRows={2} />
					<FormControlLabel
						control={<Checkbox size="small" checked={riskForm.isMaterial} onChange={(e) => setRiskForm({ ...riskForm, isMaterial: e.target.checked })} />}
						label="Material (could reasonably affect the entity's prospects)"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRiskDialog(null)}>Cancel</Button>
					<Button variant="contained" onClick={saveRisk} disabled={saving}>
						{saving ? "Saving…" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={scenarioDialog !== null} onClose={() => setScenarioDialog(null)} fullWidth maxWidth="sm">
				<DialogTitle>{scenarioDialog?.id ? "Edit scenario" : "Add scenario"}</DialogTitle>
				<DialogContent dividers sx={{ display: "grid", gap: 2, pt: 2 }}>
					<TextField size="small" label="Name" value={scenarioForm.name} onChange={(e) => setScenarioForm({ ...scenarioForm, name: e.target.value })} required sx={{ mt: 1 }} />
					<TextField size="small" label="Temperature pathway" value={scenarioForm.temperature} onChange={(e) => setScenarioForm({ ...scenarioForm, temperature: e.target.value })} helperText="e.g. 1.5°C, 3°C" />
					<TextField size="small" label="Assumptions" value={scenarioForm.assumptions} onChange={(e) => setScenarioForm({ ...scenarioForm, assumptions: e.target.value })} multiline minRows={3} />
					<TextField size="small" label="Resilience assessment" value={scenarioForm.resilienceSummary} onChange={(e) => setScenarioForm({ ...scenarioForm, resilienceSummary: e.target.value })} multiline minRows={3} />
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setScenarioDialog(null)}>Cancel</Button>
					<Button variant="contained" onClick={saveScenario} disabled={saving}>
						{saving ? "Saving…" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};

export default ClimateRisks;
